import { Component, Input, OnInit, TemplateRef, AfterViewInit, ViewChild } from '@angular/core';
import { DataTableColumn, DataTableSortDirection } from 'src/app/shared/ui/data-table/data-table.component';
import { ManagerService } from '../../../../core';
import { TranslateService } from '@ngx-translate/core';
import { StoreService } from '../../../../core/services/store.service';
import { CONSTANTS } from 'src/app/config';

@Component({
  selector: 'app-seller-preferences-table',
  templateUrl: './seller-preferences-table.component.html',
  styleUrls: ['./seller-preferences-table.component.scss']
})
export class SellerPreferencesTableComponent implements OnInit, AfterViewInit {
  @Input() userProfile: any;
  @Input() isChallengePreference!: boolean;
  @Input() salesIncentiveFlag: boolean = false;
  @Input() enablementsIncentiveFlag: boolean = false;
  @ViewChild('sellerNameTemplate') sellerNameTemplate!: TemplateRef<any>;
  @ViewChild('challengeCheckboxTemplate') challengeCheckboxTemplate!: TemplateRef<any>;
  @ViewChild('enablementsCheckboxTemplate') enablementsCheckboxTemplate!: TemplateRef<any>;
  @ViewChild('notificationsCheckboxTemplate') notificationsCheckboxTemplate!: TemplateRef<any>;

  sellerPreferencesColumns: DataTableColumn[] = [];
  sellerPreferencesRows: any[] = [];
  sellerPreferences: any;
  activitiesPage = 1;
  activitiesSize = 5;
  sellerPreferencesSortBy = '';
  sellerPreferencesSortDir: DataTableSortDirection = '';
  isSellerPreferencesLoading = false;
  searchFirstname = '';
  sortByName: any;
  userPreferences = CONSTANTS.userPreferences;

  constructor(
    private managerService: ManagerService,
    private translate: TranslateService,
    private storeService: StoreService
  ) {}

  ngOnInit(): void {
    this.getSellerPreferences();
  }

  ngAfterViewInit(): void {
    this.buildSellerPreferencesColumns();
  }

  buildSellerPreferencesColumns(): void {
    if (!this.sellerNameTemplate) {
      return;
    }

    const cols: DataTableColumn[] = [
      { field: '_serialNo', header: this.translate.instant('manageActivity.formLevel20'), align: 'left' },
      { field: 'USER_FULLNAME', header: this.translate.instant('manageActivity.formLevel15'), align: 'left', sortable: true, cellTemplate: this.sellerNameTemplate }
    ];

    if (this.isChallengePreference) {
      cols.push({ field: '_challenge', header: this.translate.instant('manageActivity.formLevel21'), align: 'center', cellTemplate: this.challengeCheckboxTemplate });
    }

    cols.push(
      { field: '_enablements', header: this.translate.instant('manageActivity.formLevel22'), align: 'center', cellTemplate: this.enablementsCheckboxTemplate },
      { field: '_notifications', header: this.translate.instant('manageActivity.formLevel23'), align: 'center', cellTemplate: this.notificationsCheckboxTemplate }
    );

    this.sellerPreferencesColumns = cols;
    this.updateRows();
  }

  updateRows(): void {
    if (!this.sellerPreferences || !this.sellerPreferences.content) {
      this.sellerPreferencesRows = [];
      return;
    }

    this.sellerPreferencesRows = this.sellerPreferences.content.map((preference: any, index: number) => {
      return Object.assign({}, preference, {
        _serialNo: this.sellerSerialNo(index),
        _index: index
      });
    });
  }

  sellerSerialNo(index: number): number {
    const page = this.activitiesPage;
    return (page - 1) * this.activitiesSize + index + 1;
  }

  getSellerPreferences(searchByFirstname?: string): void {
    if (this.userProfile && this.userProfile.company && this.userProfile.company.id) {
      this.isSellerPreferencesLoading = true;
      const sellerParams: any = {
        companyId: this.userProfile.company.id,
        page: this.activitiesPage - 1,
        size: this.activitiesSize,
      };

      if (searchByFirstname) {
        sellerParams.searchText = searchByFirstname;
      }

      if (this.sortByName) {
        sellerParams.sortBy = this.sortByName.active;
        sellerParams.sortDir = this.sortByName.direction;
      }

      this.managerService.getSellerPreferences(sellerParams).subscribe(
        (data) => {
          this.sellerPreferences = data;
          this.updateRows();
          this.isSellerPreferencesLoading = false;
        },
        (err) => {
          this.isSellerPreferencesLoading = false;
        }
      );
    }
  }

  showMoreSellerPreferences(pageNumber: number): void {
    this.activitiesPage = pageNumber;
    this.getSellerPreferences(this.searchFirstname ? this.searchFirstname : undefined);
  }

  onSellerSearch(value: string): void {
    this.searchFirstname = value;
    this.activitiesPage = 1;
    this.getSellerPreferences(value ? value : undefined);
  }

  onSellerPreferencesSort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.sellerPreferencesSortBy = event.sortBy;
    this.sellerPreferencesSortDir = event.sortDir;
    if (event.sortDir && event.sortBy) {
      this.sortByName = { active: event.sortBy, direction: event.sortDir.toUpperCase() };
    } else {
      this.sortByName = null;
    }
    this.activitiesPage = 1;
    this.getSellerPreferences();
  }

  onChallengeCheckboxChange(row: any, checked: any): void {
    if (checked === undefined || checked === null) {
      return;
    }
    this.changeSellerPreferences(row, row.user.id, { checked }, row._index, 0);
  }

  onEnablementsCheckboxChange(row: any, checked: any): void {
    if (checked === undefined || checked === null) {
      return;
    }
    this.changeSellerPreferences(row, row.user.id, { checked }, row._index, 1);
  }

  onNotificationsCheckboxChange(row: any, checked: any): void {
    if (checked === undefined || checked === null) {
      return;
    }
    this.changeSellerPreferences(row, row.user.id, { checked }, row._index, 2);
  }

  changeSellerPreferences(
    preference: any,
    userId: any,
    event: any,
    preferenceIndex: number,
    preferencesIndex: number
  ): void {
    const companyPreferencesId = this.getChallengeId(preference, preferencesIndex);
    const userPreferencesIndex = this.getChallengeIndex(preference, preferencesIndex);
    const sellerParams = {
      userId: userId,
      preferenceId: companyPreferencesId ? companyPreferencesId : 10,
      enabled: event.checked,
    };

    this.managerService.postSellerPreferences(sellerParams).subscribe(
      (data) => {
        if (
          this.sellerPreferences.content[preferenceIndex].userPreferences &&
          this.sellerPreferences.content[preferenceIndex].userPreferences[userPreferencesIndex] === undefined
        ) {
          this.showMoreSellerPreferences(this.activitiesPage);
        } else if (this.sellerPreferences.content[preferenceIndex].userPreferences) {
          this.sellerPreferences.content[preferenceIndex].userPreferences[userPreferencesIndex].enabled = data.enabled;
        }
        this.storeService.isTabActivated.next(true);
      },
      (err) => {
      }
    );
  }

  getChallengeId(item: any, index: number): any {
    if (item && item.userPreferences && item.userPreferences[index]) {
      return item.userPreferences[index].preferenceId;
    }
    return null;
  }

  getChallengeIndex(item: any, index: number): number {
    return index;
  }

  isChallengeEnable(challenge: any, index: number): boolean {
    const isEnable = challenge.userPreferences.find(
      (item: any) => item.id === this.userPreferences[index].id
    );
    return isEnable && isEnable.enabled ? true : false;
  }

  getUserProfileImageUrl(preference: any): string {
    if (preference && preference.user && preference.user.profilePicUrl) {
      return preference.user.profilePicUrl;
    }
    return '';
  }

  getUserFullName(preference: any): string {
    if (preference && preference.user) {
      const firstName = preference.user.firstName ? preference.user.firstName : '';
      const lastName = preference.user.lastName ? preference.user.lastName : '';
      return (firstName + ' ' + lastName).trim();
    }
    return '';
  }
}
